import React from 'react'
import { connect } from 'react-redux'
import { Route, Routes, Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { isEmpty } from 'lodash'
import { makeStyles } from '@mui/styles'
import { withEnv } from '@praxis/component-runtime-env'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import { withAuth, ProtectedElement } from '@praxis/component-auth'
import { getLocation } from '@praxis/runtime-utils/getLocation'

import withRouter from '../../containers/Router/WithRouter'
import Notifications from '../Notifications/Notifications'
import {
  closeSideNav,
  openSideNav,
  toggleRightSidenav,
  setSaveAlertAction,
} from '../../store/layout/actionCreator'
import AppHeader from '../Header/Header'
import Upload from '../Upload'
import NoAccessPage from '../NoAccessPage/NoAccessPage'
import './layout.css'
import { AHSideNavCanvas } from '../SideNav'
import Sidebar from './Sidebar'
import {
  selectUserEmail,
  selectUserId,
  selectUserCompany,
} from '../../store/auth/selector'
import {
  selectIsToggleRightSidenav,
  selectRightSideNavComponent,
} from '../../store/layout/selector'
import {
  fetchUserPreferences,
  updateUser,
} from '../../store/preferences/actionCreator'
import { setAuthData, closeNoAccessPopUp } from '../../store/auth/actions'
import { Search, isAuthorizedToPage } from '../../helpers/UtilityHelper'
import { isSessionActive } from '../../helpers/sessionHelper'
import { RIGHT_SIDEBAR_WIDTH } from '../../constants/search'
import firefly from '../../analytics/firefly'
import navRoutes from './routes'
import { fetchSearchMetadata } from '../../betasearch/store/search/searchSlice'
import {
  BetaAnalytics,
  trackBetaSearchAnalytics,
} from '../../analytics/betaSearch'

/**
 * Canvas 8 SideNav primary rail width constants.
 * Mirrors the CSS custom property --primaryNavWidth in c-layout.module.built.css:
 *   default (labels visible): 85px
 *   hideLabels (icon-only):   48px
 *
 * These are used to offset the header and content area so they sit to the
 * right of the fixed side-nav rail, matching what the old MUI Drawer did at 65px.
 */
const SIDENAV_WIDTH = 85   // px — labels visible

const useStyles = makeStyles(() => ({
  alertBanner: {
    paddingLeft: '4%',
  },
  bannerText: {
    justifyContent: 'center',
  },
  customBannerText: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFD63F',
    color: '#000000',
    height: '40px',
    fontSize: '16px',
    fontWeight: '500',
  },
  customButtonGreen: {
    marginLeft: '16px',
    padding: '8px',
    backgroundColor: '#1D4924',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#1D4924',
      color: '#FFFFFF',
    },
  },
  dialogContainer: {
    position: 'relative',
    minHeight: '100%',
    marginTop: 0,
  },
}))

const MyComponent = (props) => {
  const classes = useStyles()
  return <Layout {...props} classes={classes} />
}

// ─── Layout ───────────────────────────────────────────────────────────────────
export class Layout extends React.Component {
  constructor(props) {
    super(props)
    this.tab = 'home'

    this.state = {
      open: false,
    }
  }

  checksOnAuthenticationSuccess = () => {
    const {
      login = () => {},
      user: { lanId = '' },
    } = this.props
    const location = getLocation()
    const isSessionExist = isSessionActive(lanId, location.hostname)
    if (!isSessionExist) {
      login({ redirect: location.href })
    } else {
      this.onUserLoggedIn()
    }
  }

  onUserLoggedIn = () => {
    const {
      fetchUserPreferences = () => {},
      fetchSearchMetadata = () => {},
      router = {},
      auth = {},
    } = this.props
    const {
      location: { pathname: locationPath = '' },
    } = router
    fetchUserPreferences()
    fetchSearchMetadata()
    trackBetaSearchAnalytics({
      auth,
      trackingData: {
        eventType: BetaAnalytics.event.LOAD_TIME,
        eventName: BetaAnalytics.event.HOMEPAGE,
        eventData: {
          key: BetaAnalytics.event.LOAD_TIME,
          value: BetaAnalytics.event.HOMEPAGE,
        },
      },
    })
    const currentTitle = locationPath.split('/')[1] || 'home'
    if (currentTitle === Search) {
      firefly.initalizeSearchId()
    }
    const currentTab =
      ['project', 'annotator'].indexOf(currentTitle) !== -1
        ? 'projects'
        : currentTitle === 'asset-lifecycle-dashboard'
          ? 'assetLifecycleDashboard'
          : currentTitle
    this.tab = currentTab
  }

  async componentDidMount() {
    const {
      session = {},
      login = () => {},
      isAuthorized = () => {},
      setAuthData = () => {},
    } = this.props
    const { hasExpired = false } = session || {}
    if (hasExpired || !isAuthorized()) {
      const location = getLocation()
      login({ redirect: location.href })
    } else {
      await setAuthData(session)
      this.checksOnAuthenticationSuccess()
    }
  }

  componentDidUpdate(prevProps) {
    const {
      router: prevRouter = {},
      user: { lanId: prevLanId = '' },
    } = prevProps
    const { location: { pathname: prevPathname = '' } = {} } = prevRouter
    const { router = {} } = this.props
    const { location: { pathname: locationPath = '' } = {} } = router
    if (prevPathname !== locationPath) {
      this.trackView(prevPathname)
      const currentTitle = locationPath.split('/')[1]
      const previousTitle = prevPathname.split('/')[1]
      if (currentTitle !== previousTitle && currentTitle === Search) {
        firefly.initalizeSearchId()
      } else {
        if (currentTitle !== Search) {
          firefly.clearSearchId()
        }
      }
      if (currentTitle && currentTitle !== previousTitle) {
        if (['project', 'annotator'].indexOf(currentTitle) !== -1) {
          this.tab = 'projects'
        } else if (currentTitle === 'publishing') {
          this.tab = 'publish'
        } else if (currentTitle === 'asset-lifecycle-dashboard') {
          this.tab = 'assetLifecycleDashboard'
        } else {
          this.setState({ tab: currentTitle })
          this.tab = currentTitle
        }
      } else if (isEmpty(currentTitle)) {
        this.tab = 'home'
      }
    }
    const {
      auth = {},
      user: { lanId = '' },
      updateUser = () => {},
    } = this.props
    if (lanId) {
      const location = getLocation()
      isSessionActive(lanId, location.hostname)
    }
    if (prevLanId !== lanId) {
      updateUser(auth)
    }
  }

  handleNavigation = () => {
    const {
      env: { BANNER_INFO: { BANNER_REFERENCE_LINK = '' } = {} } = {},
      auth = {},
      router = {},
    } = this.props
    const { navigate = () => {} } = router
    if (BANNER_REFERENCE_LINK) {
      trackBetaSearchAnalytics({
        auth,
        trackingData: {
          eventType: BetaAnalytics.event.BANNER_CLICKED,
          eventName: BetaAnalytics.event.BANNER_CLICKED,
          eventData: {
            key: BetaAnalytics.event.BANNER_CLICKED,
            value: BANNER_REFERENCE_LINK,
          },
        },
      })
      navigate(BANNER_REFERENCE_LINK)
    }
  }

  handleMenuButtonClick = () => {
    if (this.props.sideNavIsOpen) {
      this.props.closeSideNav()
    } else {
      this.props.openSideNav()
    }
  }

  isAuthorizedPageHandler = (path = '') => {
    const { auth = {}, role = '', env = {} } = this.props
    const { memberOf = [] } = auth
    const { REACT_APP_ROLES_CONFIG = {} } = env
    const member = role ? [role] : memberOf
    return isAuthorizedToPage(path, member, REACT_APP_ROLES_CONFIG)
  }

  trackView = (prevPath) => {
    const { router = {}, user = {} } = this.props
    const { lanId = '' } = user
    const { location = {} } = router
    const { pathname } = location
    const title = pathname.split('/')[1]
    firefly.trackView(pathname, title, lanId, prevPath)
  }

  render() {
    const {
      classes = {},
      user = {},
      headerTitle,
      headerIcon,
      isToggleRightSidenav,
      rightSideNavComponent,
      toggleRightSidenav = () => {},
      setSaveAlertAction = () => {},
      auth = {},
      logout = () => {},
      router = {},
      isAuthorized = () => {},
      env = {},
    } = this.props
    const {
      location: { pathname: locationPath = '' },
    } = router
    const { REACT_APP_ROLES_CONFIG = {}, BANNER_INFO = {} } = env
    const {
      BANNER_TEXT = '',
      BANNER_ALERT_TYPE = '',
      BANNER_REFERENCE_LINK = '',
      BANNER_LINK_TEXT = '',
      BANNER_BUTTON_TEXT = '',
    } = BANNER_INFO
    const { assethubADGroups = [] } = REACT_APP_ROLES_CONFIG
    const { noAccessModalShown = false } = user
    const { isLoading = true } = auth

    const showSideNav = isAuthorized(assethubADGroups)

    // The SideNav occupies a fixed-width left rail.
    // Content (header + main) is offset to the right by that rail width.
    const navWidth = showSideNav ? SIDENAV_WIDTH : 0

    const sidebarWidth =
      isToggleRightSidenav && rightSideNavComponent
        ? RIGHT_SIDEBAR_WIDTH[rightSideNavComponent] ||
          RIGHT_SIDEBAR_WIDTH['default']
        : '0'
    const mainLayoutStyle = { width: `calc(100% - ${sidebarWidth}px)` }
    const rightSideNavStyle = { width: `${sidebarWidth}px` }

    return isAuthorized() ? (
      <div className="ah-app-root">
        <Helmet defaultTitle="AssetHub" titleTemplate="AssetHub: %s" />
        <Notifications />

        {/* ── Canvas 8 Side Navigation rail (position: fixed, left column) */}
        {showSideNav && (
          <AHSideNavCanvas
            onClose={() => this.props.closeSideNav()}
          />
        )}

        {/* ── Right-side content column (header + main), offset by nav width) */}
        <div
          className="ah-content-column"
          style={{ marginLeft: navWidth }}
        >
          {/* Alert Banner — spans full width of the content column */}
          {!locationPath.includes('annotator') &&
            !locationPath.includes('help') &&
            BANNER_TEXT && (
              <div className={classes.alertBanner}>
                <Alert
                  severity={BANNER_ALERT_TYPE}
                  className={
                    BANNER_ALERT_TYPE
                      ? classes.bannerText
                      : classes.customBannerText
                  }
                  icon={false}
                >
                  {BANNER_TEXT + ' '}
                  {BANNER_BUTTON_TEXT && BANNER_REFERENCE_LINK && (
                    <Button
                      onClick={() => this.handleNavigation()}
                      variant="outlined"
                      className={
                        BANNER_ALERT_TYPE ? '' : classes.customButtonGreen
                      }
                    >
                      {BANNER_BUTTON_TEXT}
                    </Button>
                  )}
                  {BANNER_LINK_TEXT && BANNER_REFERENCE_LINK && (
                    <Link href={BANNER_REFERENCE_LINK} target="_blank">
                      {BANNER_LINK_TEXT}
                    </Link>
                  )}
                </Alert>
              </div>
            )}

          {/* Top App Header */}
          {isAuthorized(assethubADGroups) ? (
            <AppHeader
              title={headerTitle}
              icon={headerIcon}
              menuAction={() => this.handleMenuButtonClick()}
              showFeedBack
              user={user}
              signOut={logout}
            />
          ) : (
            <AppHeader
              title={headerTitle}
              icon={headerIcon}
              showFeedBack={false}
            />
          )}

          {/* Main content area */}
          {isAuthorized() && !isLoading ? (
            <main id="main-content" className="ah-main-content">
              <Grid container>
                <Grid item style={mainLayoutStyle}>
                  {noAccessModalShown ? (
                    <NoAccessPage />
                  ) : (
                    <Routes>
                      {navRoutes.map((prop, key) => {
                        const { adGroups = () => {}, redirect = false } = prop
                        const adGroupArr = adGroups(env)
                        return (
                          <Route
                            key={key}
                            exact={prop.exact}
                            path={prop.path}
                            element={
                              redirect ? (
                                <Navigate to="/" replace />
                              ) : (
                                <ProtectedElement
                                  allowed={adGroupArr}
                                  unauthorizedRoute="/unauthorized"
                                >
                                  <prop.component
                                    style={{ indicatorColor: 'primary' }}
                                    customPath={`/${prop.displayName}`}
                                    path={prop.path}
                                  />
                                </ProtectedElement>
                              )
                            }
                          />
                        )
                      })}
                    </Routes>
                  )}
                </Grid>

                {/* Right drawer panel (Boards / Metadata / Downloads) */}
                {isToggleRightSidenav && (
                  <Grid item style={rightSideNavStyle}>
                    <Grid
                      container
                      spacing={0}
                      className={classes.dialogContainer}
                      style={{ zIndex: 99 }}
                    >
                      <Grid item xs={12}>
                        <Sidebar
                          rightSideNavComponent={rightSideNavComponent}
                          isToggleRightSidenav={isToggleRightSidenav}
                          toggleRightSidenav={toggleRightSidenav}
                          setSaveAlertAction={setSaveAlertAction}
                          router={router}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </main>
          ) : null}
        </div>

        <Upload router={router} />
        <div id="canvasPortalContainer" />
      </div>
    ) : null
  }
}

Layout.defaultProps = {
  open: true,
}

const mapStateToProps = (state = {}) => ({
  headerTitle: state.layout?.headerTitle,
  headerIcon: state.layout?.headerIcon,
  sideNavIsOpen: state.layout?.sideNavIsOpen,
  isToggleRightSidenav: selectIsToggleRightSidenav()(state),
  rightSideNavComponent: selectRightSideNavComponent()(state),
  user: {
    email: selectUserEmail()(state),
    noAccessModalShown: state.auth?.noAccessModalShown,
    lanId: selectUserId()(state),
    company: selectUserCompany()(state),
  },
  userPreferences: state.userPreferences,
  auth: state.auth,
  role: state.headerReducer?.role,
})

const mapDispatchToProps = {
  openSideNav,
  closeSideNav,
  toggleRightSidenav,
  setSaveAlertAction,
  fetchUserPreferences,
  fetchSearchMetadata,
  closeNoAccessPopUp,
  updateUser,
  setAuthData,
}

const mapAuthToProps = (auth) => ({
  ...auth,
})

export default withAuth(mapAuthToProps)(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withEnv()(withRouter(MyComponent)))
)
